export const CONTENT_SYSTEM_FOLDER_TYPES = {
  brands: 'content_brand_profiles',
  campaigns: 'content_campaigns',
  assets: 'content_assets',
  outputs: 'content_outputs'
} as const

export const CONTENT_SYSTEM_PROMPT_TYPES = {
  brand: 'content-system:brand-profile',
  campaign: 'content-system:campaign',
  output: 'content-system:asset-pack',
  executionDoc: 'content-system:execution-doc'
} as const

export const CONTENT_SYSTEM_FOLDER_NAMES = {
  [CONTENT_SYSTEM_FOLDER_TYPES.brands]: 'Content System Brands',
  [CONTENT_SYSTEM_FOLDER_TYPES.campaigns]: 'Content System Campaigns',
  [CONTENT_SYSTEM_FOLDER_TYPES.assets]: 'Content System Assets',
  [CONTENT_SYSTEM_FOLDER_TYPES.outputs]: 'Content System Outputs'
} as const

export type ContentPlatform =
  | 'blog'
  | 'linkedin'
  | 'x'
  | 'instagram'
  | 'newsletter'
  | 'video'
  | 'image'

export type BrandProfileStatus = 'active' | 'archived'
export type BrandGuidelineIterationMode = 'generated' | 'iterated'

export interface BrandColorToken {
  id: string
  name: string
  hex: string
  role: string
  usage: string
}

export interface BrandTypographySettings {
  primaryFont: string
  secondaryFont: string
  accentFont: string
  headingStyle: string
  bodyStyle: string
}

export interface BrandLogoGuidelines {
  clearspace: string
  minimumSize: string
  backgroundRules: string
  approvedLockups: string[]
  dos: string[]
  donts: string[]
}

export interface BrandSocialProductionGuidelines {
  designPrinciples: string[]
  approvedFormats: string[]
  overlayRules: string[]
  ctaTreatments: string[]
  accessibilityRules: string[]
  motionRules: string[]
  imageStyleRules: string[]
  prohibitedVisuals: string[]
}

export interface BrandGuidelineSnapshot {
  name: string
  companyName: string
  audience: string
  offer: string
  brandSummary: string
  voice: string
  pillars: string[]
  dos: string[]
  donts: string[]
  bannedWords: string[]
  preferredPhrases: string[]
  visualDirection: string
  brandColors: BrandColorToken[]
  typography: BrandTypographySettings
  logoGuidelines: BrandLogoGuidelines
  socialProduction: BrandSocialProductionGuidelines
  websiteUrls: string[]
  notes: string
  guidelineMarkdown: string
}

export interface BrandGuidelineIteration {
  id: string
  label: string
  mode: BrandGuidelineIterationMode
  request: string
  summary: string
  changes: string[]
  guidelineMarkdown: string
  snapshot: BrandGuidelineSnapshot
  createdAt: string
}

export interface BrandGuidelineGenerationResult extends BrandGuidelineSnapshot {
  summary: string
  changes: string[]
}

export type ContentWorkflowStatus =
  | 'draft'
  | 'generating'
  | 'generated'
  | 'in_review'
  | 'approved'
  | 'archived'

export type ContentActivityType =
  | 'created'
  | 'updated'
  | 'generated'
  | 'status_changed'
  | 'execution_doc_created'
  | 'wordpress_draft_created'
  | 'duplicated'
  | 'archived'

export interface ContentActivityEntry {
  id: string
  type: ContentActivityType
  message: string
  timestamp: string
  actorEmail: string | null
}

export interface BrandProfile {
  id?: string
  kind: 'brand-profile'
  name: string
  companyName: string
  audience: string
  offer: string
  brandSummary: string
  voice: string
  pillars: string[]
  dos: string[]
  donts: string[]
  bannedWords: string[]
  preferredPhrases: string[]
  visualDirection: string
  brandColors: BrandColorToken[]
  typography: BrandTypographySettings
  logoGuidelines: BrandLogoGuidelines
  socialProduction: BrandSocialProductionGuidelines
  websiteUrls: string[]
  notes: string
  guidelineMarkdown: string
  guidelineIterations: BrandGuidelineIteration[]
  activeGuidelineIterationId: string | null
  brandGuidelineFileIds: string[]
  inspirationFileIds: string[]
  brandLogoFileIds: string[]
  socialTemplateFileIds: string[]
  status: BrandProfileStatus
  archivedAt: string | null
  lastUsedAt: string | null
  activityLog: ContentActivityEntry[]
  createdAt: string
  updatedAt: string
}

export interface ContentCalendarAsset {
  day: string
  platform: string
  format: string
  title: string
  hook: string
  summary: string
  cta: string
}

export interface ContentCalendarWeek {
  week: number
  theme: string
  goal: string
  assets: ContentCalendarAsset[]
}

export interface ContentStrategy {
  campaignTitle: string
  bigIdea: string
  coreNarrative: string
  audienceHook: string
  contentPillars: string[]
  callsToAction: string[]
  voiceReminders: string[]
}

export type ContentBlogVariantMode = 'generated' | 'iterated'

export interface ContentBlogVariant {
  id: string
  label: string
  mode: ContentBlogVariantMode
  request: string
  summary: string
  title: string
  metaDescription: string
  outline: string[]
  draft: string
  createdAt: string
}

export interface ContentCarouselSlide {
  id: string
  title: string
  prompt: string
}

export interface ContentCarouselPlan {
  id: string
  title: string
  platform: string
  slides: ContentCarouselSlide[]
}

export type ContentGeneratedAssetKind =
  | 'image'
  | 'carousel-slide'
  | 'video'
  | 'reel'
  | 'edited-image'

export interface ContentGeneratedAssetMetadata {
  fileId: string
  kind: ContentGeneratedAssetKind
  label: string
  createdAt: string
  providerJobId: string | null
  collectionId?: string | null
  collectionLabel?: string | null
  sequenceIndex?: number | null
  sequenceTotal?: number | null
  useAsGenerationReference?: boolean
}

export interface ContentVisualPack {
  imagePrompts: string[]
  carouselPlans: ContentCarouselPlan[]
  videoStoryboard: string[]
  videoPrompt: string
  reelPrompt: string
  reelStoryboard: string[]
  thumbnailIdeas: string[]
  generatedAssets: ContentGeneratedAssetMetadata[]
}

export interface ContentSocialPack {
  linkedin: string[]
  x: string[]
  instagramCaptions: string[]
  instagramReelScript: string
  newsletterSnippet: string
}

export interface ContentAssetPack {
  strategy: ContentStrategy
  calendar: ContentCalendarWeek[]
  blog: {
    title: string
    metaDescription: string
    outline: string[]
    draft: string
    variants: ContentBlogVariant[]
    activeVariantId: string | null
  }
  social: ContentSocialPack
  visuals: ContentVisualPack
  repurposing: string[]
  qualityChecklist: string[]
  generatedAt: string
  model: string
}

export type ContentExecutionDocType =
  | 'campaign-runbook'
  | 'blog-draft'
  | 'social-pack'
  | 'visual-pack'
  | 'newsletter-pack'

export interface ContentExecutionDoc {
  id?: string
  kind: 'execution-doc'
  name: string
  docType: ContentExecutionDocType
  campaignId: string | null
  outputRecordId: string | null
  brandProfileId: string | null
  platform: ContentPlatform | null
  content: string
  createdAt: string
  updatedAt: string
}

export interface ContentOutputRecord {
  id?: string
  kind: 'asset-pack'
  name: string
  campaignId: string | null
  brandProfileId: string | null
  sourceCampaignName: string
  topic: string
  status: Extract<ContentWorkflowStatus, 'generated' | 'in_review' | 'approved' | 'archived'>
  model: string
  assetPack: ContentAssetPack
  executionDocIds: string[]
  generatedAssetFileIds: string[]
  wordpressDraftId: number | null
  wordpressDraftUrl: string | null
  activityLog: ContentActivityEntry[]
  createdAt: string
  updatedAt: string
}

export interface ContentCampaign {
  id?: string
  kind: 'campaign'
  name: string
  topic: string
  primaryObjective: string
  audience: string
  offer: string
  keyMessage: string
  callToAction: string
  additionalContext: string
  campaignWeeks: number
  postsPerWeek: number
  platforms: ContentPlatform[]
  brandProfileId: string | null
  referenceFileIds: string[]
  status: ContentWorkflowStatus
  outputs: ContentAssetPack | null
  outputRecordIds: string[]
  lastGeneratedOutputId: string | null
  reviewNotes: string
  activityLog: ContentActivityEntry[]
  createdAt: string
  updatedAt: string
}

export type ContentSystemRecord =
  | BrandProfile
  | ContentCampaign
  | ContentOutputRecord
  | ContentExecutionDoc
